import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, ShieldCheck, Mail, Cloud, MessageCircle, Building2, Percent, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const SInput = ({ label, value, onChange, type = "text", placeholder, hint, testid }) => (
  <label className="block">
    <span className="overline text-[10px] text-muted-foreground">{label}</span>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
      className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-cream focus:border-petrol outline-none" />
    {hint && <span className="text-[11px] text-muted-foreground mt-1 block">{hint}</span>}
  </label>
);

const SCheckbox = ({ label, checked, onChange, hint, testid }) => (
  <label className="flex items-start gap-3 sm:col-span-2 cursor-pointer">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid}
      className="mt-1 w-4 h-4 accent-petrol" />
    <span>
      <span className="text-sm text-petrol font-medium">{label}</span>
      {hint && <span className="text-[11px] text-muted-foreground mt-0.5 block">{hint}</span>}
    </span>
  </label>
);

const STextarea = ({ label, value, onChange, hint, testid, rows = 5 }) => (
  <label className="block sm:col-span-2">
    <span className="overline text-[10px] text-muted-foreground">{label}</span>
    <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testid}
      className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-cream focus:border-petrol outline-none font-mono leading-relaxed" />
    {hint && <span className="text-[11px] text-muted-foreground mt-1 block">{hint}</span>}
  </label>
);

const Section = ({ icon: Icon, title, desc, children }) => (
  <div className="bg-card border border-line rounded-sm p-7 mb-6">
    <div className="flex items-start gap-3 mb-6">
      <span className="w-10 h-10 rounded-sm bg-beige flex items-center justify-center text-gold shrink-0"><Icon size={18} strokeWidth={1.5} /></span>
      <div>
        <h2 className="font-serif text-2xl text-petrol leading-none">{title}</h2>
        {desc && <p className="text-sm text-muted-foreground mt-1">{desc}</p>}
      </div>
    </div>
    <div className="grid sm:grid-cols-2 gap-5">{children}</div>
  </div>
);

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      const data = r.data;
      // Auto-fill public site URL (used for auto pre-arrival check-in links)
      if (!data.site_base_url) {
        const origin = window.location.origin;
        api.put("/admin/settings", { site_base_url: origin }).catch(() => {});
        data.site_base_url = origin;
      }
      setS(data);
    });
  }, []);
  if (!s) return <p className="text-muted-foreground">Caricamento...</p>;

  const set = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setSaving(true);
    const payload = { ...s };
    delete payload.webdav_password_set; delete payload.smtp_password_set; delete payload.id;
    if (!payload.webdav_password) delete payload.webdav_password;
    if (!payload.smtp_password) delete payload.smtp_password;
    try {
      const r = await api.put("/admin/settings", payload);
      setS(r.data);
      toast.success("Impostazioni salvate");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const testWebdav = async () => {
    const r = await api.post("/admin/settings/test-webdav");
    r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message);
  };

  return (
    <div data-testid="admin-settings" className="max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif font-light text-4xl text-petrol">Impostazioni</h1>
          <p className="text-muted-foreground">Configura contatti, storage documenti ed email</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-settings" className="flex items-center gap-2 bg-petrol text-white px-6 py-3 rounded-sm hover:bg-[#1A3A41] transition-colors disabled:opacity-60">
          <Save size={18} strokeWidth={1.5} /> {saving ? "Salvataggio..." : "Salva"}
        </button>
      </div>

      <Section icon={Building2} title="Brand & Contatti" desc="Informazioni mostrate sul sito pubblico">
        <SInput label="Nome brand" value={s.brand_name} onChange={(v) => set("brand_name", v)} testid="set-brand" />
        <SInput label="Claim / Slogan" value={s.claim} onChange={(v) => set("claim", v)} />
        <SInput label="Email di contatto" value={s.contact_email} onChange={(v) => set("contact_email", v)} testid="set-contact-email" />
        <SInput label="Telefono" value={s.contact_phone} onChange={(v) => set("contact_phone", v)} />
        <SInput label="Indirizzo" value={s.address} onChange={(v) => set("address", v)} />
        <SInput label="Link mappa (embed Google Maps)" value={s.map_embed} onChange={(v) => set("map_embed", v)} />
      </Section>

      <Section icon={MessageCircle} title="WhatsApp & Social" desc="Pulsante WhatsApp flottante e link social">
        <SInput label="Numero WhatsApp (con prefisso, es. +39...)" value={s.whatsapp_number} onChange={(v) => set("whatsapp_number", v)} testid="set-whatsapp" placeholder="+39 333 1234567" />
        <SInput label="Email notifiche admin" value={s.admin_notify_email} onChange={(v) => set("admin_notify_email", v)} hint="Riceve le notifiche di nuove richieste e check-in" />
        <SInput label="Instagram (URL)" value={s.social_instagram} onChange={(v) => set("social_instagram", v)} />
        <SInput label="Facebook (URL)" value={s.social_facebook} onChange={(v) => set("social_facebook", v)} />
      </Section>

      <Section icon={Cloud} title="Aruba Drive / Nextcloud (WebDAV)" desc="Spazio cloud sicuro dove vengono salvati i documenti del check-in. I file non vengono mai conservati sul sito.">
        <SInput label="URL WebDAV" value={s.webdav_base_url} onChange={(v) => set("webdav_base_url", v)} testid="set-webdav-url"
          placeholder="https://drive.aruba.it/remote.php/dav/files/UTENTE" hint="Per Nextcloud: .../remote.php/dav/files/NOMEUTENTE" />
        <SInput label="Cartella radice" value={s.webdav_root_folder} onChange={(v) => set("webdav_root_folder", v)} placeholder="Fiorucci Apartments" />
        <SInput label="Username WebDAV" value={s.webdav_username} onChange={(v) => set("webdav_username", v)} testid="set-webdav-user" />
        <SInput label={`Password WebDAV ${s.webdav_password_set ? "(impostata — lascia vuoto per non cambiare)" : ""}`} type="password" value={s.webdav_password} onChange={(v) => set("webdav_password", v)} testid="set-webdav-pass" />
        <div className="sm:col-span-2">
          <button onClick={testWebdav} data-testid="test-webdav" className="flex items-center gap-2 text-sm border border-petrol text-petrol px-4 py-2 rounded-sm hover:bg-beige transition-colors">
            <ShieldCheck size={16} /> Testa connessione
          </button>
        </div>
      </Section>

      <Section icon={Mail} title="Email Aruba (SMTP)" desc="Invio automatico email di conferma e notifiche. Usa il server SMTP della tua casella Aruba.">
        <SInput label="Server SMTP" value={s.smtp_host} onChange={(v) => set("smtp_host", v)} placeholder="smtps.aruba.it" />
        <SInput label="Porta" type="number" value={s.smtp_port} onChange={(v) => set("smtp_port", Number(v))} placeholder="465" hint="465 (SSL) consigliata per Aruba" />
        <SInput label="Username (email completa)" value={s.smtp_username} onChange={(v) => set("smtp_username", v)} testid="set-smtp-user" />
        <SInput label={`Password ${s.smtp_password_set ? "(impostata — lascia vuoto per non cambiare)" : ""}`} type="password" value={s.smtp_password} onChange={(v) => set("smtp_password", v)} testid="set-smtp-pass" />
        <SInput label="Mittente (From)" value={s.smtp_from} onChange={(v) => set("smtp_from", v)} hint="Solitamente uguale allo username" />
        <SInput label="Limite dimensione file (MB)" type="number" value={s.max_file_mb} onChange={(v) => set("max_file_mb", Number(v))} />
      </Section>

      <Section icon={Percent} title="Analytics & ROI — Valori predefiniti" desc="Usati per calcolare automaticamente commissioni e tasse delle nuove prenotazioni (modificabili per ogni singola prenotazione).">
        <SInput label="Costo pulizie predefinito (€)" type="number" value={s.default_cleaning_cost} onChange={(v) => set("default_cleaning_cost", Number(v))} testid="set-cleaning" />
        <SInput label="Aliquota tasse (%)" type="number" value={s.tax_pct} onChange={(v) => set("tax_pct", Number(v))} hint="Es. cedolare secca 21%" testid="set-tax-pct" />
        <SInput label="Commissione Airbnb (%)" type="number" value={s.airbnb_fee_pct} onChange={(v) => set("airbnb_fee_pct", Number(v))} />
        <SInput label="Commissione Booking (%)" type="number" value={s.booking_fee_pct} onChange={(v) => set("booking_fee_pct", Number(v))} />
        <SInput label="Commissione altre piattaforme (%)" type="number" value={s.other_fee_pct} onChange={(v) => set("other_fee_pct", Number(v))} />
      </Section>

      <Section icon={Send} title="Messaggio pre-arrivo" desc="Email inviata agli ospiti prima dell'arrivo con regole della casa, Wi-Fi e link al check-in. Segnaposto disponibili: {guest_name}, {apartment_name}, {arrival_date}, {wifi_name}, {wifi_password}, {house_rules}, {checkin_instructions}, {checkin_link}, {brand_name}.">
        <SInput label="Oggetto email" value={s.prearrival_subject} onChange={(v) => set("prearrival_subject", v)} testid="set-prearrival-subject" />
        <STextarea label="Corpo email (HTML)" value={s.prearrival_template} onChange={(v) => set("prearrival_template", v)} testid="set-prearrival-template" rows={10}
          hint="I segnaposto verranno sostituiti automaticamente con i dati dell'appartamento e dell'ospite." />
        <SCheckbox label="Invio automatico pre-arrivo" checked={s.prearrival_auto_enabled} onChange={(v) => set("prearrival_auto_enabled", v)} testid="set-prearrival-auto"
          hint="Invia automaticamente l'email agli ospiti con check-in non completato, alcuni giorni prima dell'arrivo (controllo giornaliero alle 09:00 UTC)." />
        <SInput label="Giorni prima dell'arrivo" type="number" value={s.prearrival_days_before} onChange={(v) => set("prearrival_days_before", Number(v))} testid="set-prearrival-days" hint="Es. 3 = invio 3 giorni prima dell'arrivo." />
        <SInput label="URL pubblico del sito" value={s.site_base_url} onChange={(v) => set("site_base_url", v)} testid="set-site-url" hint="Usato per costruire il link di check-in nelle email automatiche." />
      </Section>

      <Section icon={Send} title="Promemoria check-in (sollecito)" desc="Se l'ospite non completa il check-in dopo aver ricevuto il messaggio pre-arrivo, viene inviato automaticamente un sollecito. Stessi segnaposto del messaggio pre-arrivo.">
        <SCheckbox label="Sollecito automatico" checked={s.prearrival_reminder_enabled} onChange={(v) => set("prearrival_reminder_enabled", v)} testid="set-reminder-auto"
          hint="Invia un promemoria agli ospiti con check-in non completato (e arrivo non ancora trascorso) dopo i giorni indicati." />
        <SInput label="Giorni dopo il pre-arrivo" type="number" value={s.prearrival_reminder_days} onChange={(v) => set("prearrival_reminder_days", Number(v))} testid="set-reminder-days" hint="Es. 2 = sollecito 2 giorni dopo l'invio del messaggio pre-arrivo." />
        <SInput label="Oggetto sollecito" value={s.reminder_subject} onChange={(v) => set("reminder_subject", v)} testid="set-reminder-subject" />
        <STextarea label="Corpo sollecito (HTML)" value={s.reminder_template} onChange={(v) => set("reminder_template", v)} testid="set-reminder-template" rows={8} />
      </Section>
    </div>
  );
}
